import React, { useMemo, useState } from "react";
import { AutoComplete, Input, Spin } from "antd";
import {
  SearchOutlined,
  FileOutlined,
  TeamOutlined,
  UserOutlined,
  BarChartOutlined,
  MailOutlined,
} from "@ant-design/icons";
import { injectIntl } from "react-intl";
import { withRouter } from "react-router-dom";
import useGlobalSearch from "../../../hooks/entities/globalSearch";
import { filterPages } from "../menus/pageSearchIndex";
import styles from "./index.module.css";

/**
 * Encodes a search hit as an AutoComplete option value.
 * @param {string} type Result type (page, patient, prescriber, analysis, invitation).
 * @param {string} id Entity id.
 * @returns {string} Encoded option value.
 */
const encodeOptionValue = (type, id) => `${type}::${id}`;

/**
 * Splits an encoded AutoComplete option value into type and id.
 * @param {string} value Encoded option value.
 * @returns {{ type: string, id: string }} Decoded type and id.
 */
const decodeOptionValue = (value) => {
  const [type, ...rest] = (value || "").split("::");
  return { type, id: rest.join("::") };
};

const GlobalSearch = ({
  intl,
  history,
  userId,
  autoFocus = false,
  onNavigate,
  className,
  style,
}) => {
  const translate = (id) => intl.formatMessage({ id });
  const { term, results, loading, search, clear } = useGlobalSearch();
  const [open, setOpen] = useState(false);

  const pages = useMemo(
    () => filterPages(term, translate, userId),
    [term, intl.locale, userId],
  );

  const optionLookup = useMemo(() => {
    const map = {};

    pages.forEach((page) => {
      map[encodeOptionValue("page", page.id)] = { type: "page", ...page };
    });
    (results.patients || []).forEach((item) => {
      map[encodeOptionValue("patient", item.id)] = {
        type: "patient",
        ...item,
      };
    });
    (results.prescribers || []).forEach((item) => {
      map[encodeOptionValue("prescriber", item.id)] = {
        type: "prescriber",
        ...item,
      };
    });
    (results.analyses || []).forEach((item) => {
      map[encodeOptionValue("analysis", item.id)] = {
        type: "analysis",
        ...item,
      };
    });
    (results.invitations || []).forEach((item) => {
      map[encodeOptionValue("invitation", item.id)] = {
        type: "invitation",
        ...item,
      };
    });

    return map;
  }, [pages, results]);

  const options = useMemo(() => {
    const list = [];

    const pushGroup = (title, items, renderItem) => {
      if (!items.length) {
        return;
      }
      list.push({
        value: `__header__${title}`,
        disabled: true,
        className: styles.groupHeader,
        label: title,
      });
      items.forEach((item) => list.push(renderItem(item)));
    };

    pushGroup(translate("search_pages"), pages, (page) => ({
      value: encodeOptionValue("page", page.id),
      label: (
        <div className={styles.option}>
          <FileOutlined className={styles.optionIcon} />
          <div className={styles.optionText}>
            <div className={styles.optionTitle}>{page.label}</div>
            {page.description ? (
              <div className={styles.optionSub}>{page.description}</div>
            ) : null}
          </div>
        </div>
      ),
    }));

    pushGroup(translate("search_patients"), results.patients || [], (item) => ({
      value: encodeOptionValue("patient", item.id),
      label: (
        <div className={styles.option}>
          <TeamOutlined className={styles.optionIcon} />
          <div className={styles.optionText}>
            <div className={styles.optionTitle}>{item.name || item.email}</div>
            {item.email ? <div className={styles.optionSub}>{item.email}</div> : null}
          </div>
        </div>
      ),
    }));

    pushGroup(translate("search_prescribers"), results.prescribers || [], (item) => ({
      value: encodeOptionValue("prescriber", item.id),
      label: (
        <div className={styles.option}>
          <UserOutlined className={styles.optionIcon} />
          <div className={styles.optionText}>
            <div className={styles.optionTitle}>{item.name || item.email}</div>
            {item.email ? <div className={styles.optionSub}>{item.email}</div> : null}
          </div>
        </div>
      ),
    }));

    pushGroup(translate("search_analyses"), results.analyses || [], (item) => ({
      value: encodeOptionValue("analysis", item.id),
      label: (
        <div className={styles.option}>
          <BarChartOutlined className={styles.optionIcon} />
          <div className={styles.optionText}>
            <div className={styles.optionTitle}>{item.name || translate("analysis")}</div>
            <div className={styles.optionSub}>
              {item.completed ? translate("completed") : translate("not_completed")}
              {item.createdAt
                ? ` · ${new Date(item.createdAt).toLocaleDateString()}`
                : ""}
            </div>
          </div>
        </div>
      ),
    }));

    pushGroup(translate("search_invitations"), results.invitations || [], (item) => ({
      value: encodeOptionValue("invitation", item.id),
      label: (
        <div className={styles.option}>
          <MailOutlined className={styles.optionIcon} />
          <div className={styles.optionText}>
            <div className={styles.optionTitle}>{item.name || item.email}</div>
            {item.email ? <div className={styles.optionSub}>{item.email}</div> : null}
          </div>
        </div>
      ),
    }));

    if (!list.length && term.trim().length >= 1 && !loading) {
      return [
        {
          value: "__empty__",
          disabled: true,
          label: <span className={styles.empty}>{translate("search_no_results")}</span>,
        },
      ];
    }

    return list;
  }, [pages, results, term, loading, intl.locale]);

  const handleSelect = (value) => {
    if (!value || value === "__empty__" || String(value).startsWith("__header__")) {
      return;
    }

    const { type } = decodeOptionValue(value);
    const item = optionLookup[value];
    if (!item) {
      return;
    }

    if (type === "page") {
      history.push(item.path);
    } else if (type === "analysis") {
      history.push(`/user/${item.userId}/answer-questionnaire/history/${item.id}`);
    } else if (type === "patient") {
      history.push({
        pathname: "/admin/pacients",
        state: { presetSearch: item.name || item.email },
      });
    } else if (type === "prescriber") {
      history.push({
        pathname: "/admin/prescribers",
        state: { presetSearch: item.name || item.email },
      });
    } else if (type === "invitation") {
      history.push({
        pathname: "/admin/invites",
        state: { presetSearch: item.name || item.email },
      });
    }

    clear();
    setOpen(false);
    if (onNavigate) {
      onNavigate();
    }
  };

  return (
    <div className={`${styles.wrapper} ${className || ""}`} style={style}>
      <AutoComplete
        className={styles.autoComplete}
        value={term}
        open={open}
        onDropdownVisibleChange={setOpen}
        options={options}
        filterOption={false}
        onSearch={search}
        onSelect={handleSelect}
        onChange={(value) => {
          if (
            typeof value === "string" &&
            (value.includes("::") || value.startsWith("__"))
          ) {
            return;
          }
          search(value);
        }}
        defaultActiveFirstOption={false}
        dropdownMatchSelectWidth={true}
        dropdownClassName={styles.dropdown}
      >
        <Input
          prefix={loading ? <Spin size="small" /> : <SearchOutlined />}
          placeholder={translate("search_placeholder")}
          allowClear
          autoFocus={autoFocus}
          onPressEnter={() => {
            const firstKey = Object.keys(optionLookup)[0];
            if (firstKey) {
              handleSelect(firstKey);
            }
          }}
        />
      </AutoComplete>
    </div>
  );
};

export default withRouter(injectIntl(GlobalSearch));
